import React from "react";
import { notification } from "../../../../utils/Notifications.tsx";
import Loader from "../../../Loader.tsx";
import { getOperationalRiskReport } from "../../../../services/branchSchedule.service.ts";
import moment from "moment-timezone";
import RiesgoOperativoRow from "./RiesgoOperativoRow.tsx";
import ModalContainer from "../../../ModalContainer.tsx";
import { toCamelCase } from "../../../../hooks/toCameCase.tsx";

interface BranchData {
  id: number;
  name: string;
  chilitosOperacion: {
    matutinos: { actuales: number; requeridos: number };
    intermedios: { actuales: number; requeridos: number };
    vespertinos: { actuales: number; requeridos: number };
  };
  puestosCubiertos: {
    matutinos: { cubiertos: number; total: number };
    intermedios: { cubiertos: number; total: number };
    vespertinos: { cubiertos: number; total: number };
  };
  faltasDia: number;
  coberturaFaltas: {
    cubiertas: number;
    total: number;
    porcentaje: number;
  };
  chilitosDobleFuncion: number;
  details: {
    operacionList: any[];
    faltantesList: any[];
    proximosList: any[];
    puestosList: any[];
    coberturaList: any[];
    dobleFuncionList: any[];
  };
}

const TableRiesgoOperativo = () => {
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState<BranchData[]>([]);

  // Search and Sort State
  const [search, setSearch] = React.useState("");
  const [sortBy, setSortBy] = React.useState("name");
  const [order, setOrder] = React.useState<"asc" | "desc">("asc");

  // Modal State
  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalTitle, setModalTitle] = React.useState("");
  const [modalType, setModalType] = React.useState<string>("");
  const [operacionTab, setOperacionTab] = React.useState<
    "proximos" | "operacion" | "faltantes"
  >("operacion");
  const [selectedBranch, setSelectedBranch] = React.useState<BranchData | null>(
    null
  );

  const fetchData = async () => {
    try {
      setLoading(true);
      const today = moment()
        .tz("America/Mexico_City")
        .format("YYYY-MM-DD");

      const query = `date=${today}&search=${search}&sortBy=${sortBy}&order=${order}&limit=100`;
      const response = await getOperationalRiskReport(query);

      setData(response.data || []);
    } catch (error: any) {
      notification(
        "error",
        error.message || "Error al obtener datos",
        "danger"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (branch: BranchData, type: string) => {
    setSelectedBranch(branch);
    setModalType(type);
    if (type === "operacion") {
      setOperacionTab("operacion");
    }
    setModalOpen(true);

    switch (type) {
      case "operacion":
        setModalTitle(`Chilitos en Operación - ${branch.name}`);
        break;
      case "puestos":
        setModalTitle(`Puestos Cubiertos - ${branch.name}`);
        break;
      case "faltas":
        setModalTitle(`Faltas del Día - ${branch.name}`);
        break;
      case "cobertura":
        setModalTitle(`Cobertura de Faltas - ${branch.name}`);
        break;
      case "doble":
        setModalTitle(`Chilitos con Doble Función - ${branch.name}`);
        break;
      default:
        setModalTitle("Detalles");
    }
  };

  const handleSort = (field: string) => {
    const newOrder = sortBy === field && order === "asc" ? "desc" : "asc";
    setSortBy(field);
    setOrder(newOrder);
  };

  const getIconSort = (field: string) => {
    if (sortBy !== field)
      return <i style={{ opacity: 0.3 }}></i>;
    return (
      <i
        className={`fa-solid fa-sort-${order === "asc" ? "up" : "down"} ms-1`}
        style={{ color: "white" }}
      />
    );
  };

  React.useEffect(() => {
    const timer = setTimeout(() => {
      fetchData();
    }, 500); // Debounce search
    return () => clearTimeout(timer);
  }, [search, sortBy, order]);

  const formatPartnerName = (partner: any) => {
    if (!partner || !partner.person) return "-";
    const { firstName, lastName, secondLastName } = partner.person;
    const fullName = `${firstName || ""} ${lastName || ""} ${secondLastName || ""
      }`.trim();
    if (!fullName) return "-";
    return toCamelCase(fullName);
  };

  const renderPartnerWithIcon = (partner: any) => (
    <span
      style={{
        display: "flex",
        width: "100%",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: "8px",
      }}
    >
      <i
        className="fa-solid fa-user"
        style={{ color: "#6B7280", fontSize: "12px" }}
        aria-hidden="true"
      ></i>
      <span>{formatPartnerName(partner)}</span>
    </span>
  );

  const formatSimpleTime = (value?: string) => {
    const raw = String(value || "").trim();
    if (!raw) return "-";
    const m = raw.match(/\d{1,2}:\d{2}(?::\d{2})?/);
    const token = m ? m[0] : raw;
    if (moment(token, "HH:mm:ss", true).isValid()) {
      return moment(token, "HH:mm:ss").format("HH:mm");
    }
    if (moment(token, "HH:mm", true).isValid()) {
      return moment(token, "HH:mm").format("HH:mm");
    }
    return token.slice(0, 5);
  };

  const formatScheduleRange = (startTime?: string, endTime?: string) => {
    const text = `${startTime || ""} ${endTime || ""}`.trim();
    const matches = text.match(/\d{1,2}:\d{2}(?::\d{2})?/g) || [];
    if (matches.length === 0) return "-";
    const first = formatSimpleTime(matches[0]);
    const last = formatSimpleTime(matches[matches.length - 1]);
    return first === last ? first : `${first} - ${last}`;
  };

  const getShiftBadgeStyle = (shift: string): React.CSSProperties => {
    const normalized = String(shift || "").toLowerCase();
    if (normalized === "matutino") {
      return { background: "#FEF3C7", color: "#92400E" };
    }
    if (normalized === "intermedio") {
      return { background: "#DBEAFE", color: "#1D4ED8" };
    }
    return { background: "#FEE2E2", color: "#B91C1C" };
  };

  const renderModalContent = () => {
    if (!selectedBranch) return null;

    const { details } = selectedBranch;

    const tableHeaderStyle: React.CSSProperties = {
      backgroundColor: "#f8f9fa",
      padding: "12px",
      fontWeight: "600",
      borderBottom: "2px solid #dee2e6",
      textAlign: "center",
    };

    const tableCellStyle: React.CSSProperties = {
      padding: "12px",
      borderBottom: "1px solid #dee2e6",
      textAlign: "center",
      verticalAlign: "middle",
    };
    const tableMinHeightPx = 480;

    if (modalType === "operacion") {
      const inOperation = [...(details.operacionList || [])].sort(
        (a: any, b: any) => {
          const aTime = String(a?.assistanceCheck?.checkTime || "");
          const bTime = String(b?.assistanceCheck?.checkTime || "");
          if (!aTime && !bTime) return 0;
          if (!aTime) return 1;
          if (!bTime) return -1;
          return aTime.localeCompare(bTime);
        },
      );
      const operationTabButtonStyle = (
        tab: "proximos" | "operacion" | "faltantes",
      ): React.CSSProperties => ({
        border: "1px solid #D1D5DB",
        background:
          tab === "operacion"
            ? operacionTab === tab
              ? "#22C55E"
              : "#DCFCE7"
            : tab === "proximos"
            ? operacionTab === tab
              ? "#0EA5E9"
              : "#E0F2FE"
            : operacionTab === tab
            ? "#EF4444"
            : "#FEE2E2",
        color:
          operacionTab === tab
            ? "#FFFFFF"
            : tab === "operacion"
            ? "#166534"
            : tab === "proximos"
            ? "#0369A1"
            : "#B91C1C",
        boxShadow:
          operacionTab === tab
            ? "0 2px 8px rgba(15, 23, 42, 0.18)"
            : "none",
        borderRadius: "8px",
        padding: "8px 12px",
        fontSize: "12px",
        fontWeight: 700,
        cursor: "pointer",
      });

      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <button
              type="button"
              style={operationTabButtonStyle("proximos")}
              onClick={() => setOperacionTab("proximos")}
            >
              Próximos Programados ({details.proximosList?.length || 0})
            </button>
            <button
              type="button"
              style={operationTabButtonStyle("operacion")}
              onClick={() => setOperacionTab("operacion")}
            >
              En Operacion ({inOperation.length})
            </button>
            <button
              type="button"
              style={operationTabButtonStyle("faltantes")}
              onClick={() => setOperacionTab("faltantes")}
            >
              Faltantes por Entrar ({details.faltantesList.length})
            </button>
          </div>

          {operacionTab === "proximos" && (
            <div>
              <h6
                style={{
                  fontWeight: "700",
                  marginBottom: "12px",
                  color: "#17a2b8",
                }}
              >
                Próximos Programados ({details.proximosList?.length || 0})
              </h6>
              <div
                style={{
                  overflowX: "auto",
                  maxHeight: "320px",
                  minHeight: `${tableMinHeightPx}px`,
                  border: "1px solid #E5E7EB",
                  borderRadius: "10px",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "separate",
                    borderSpacing: 0,
                  }}
                >
                  <thead>
                    <tr>
                      <th style={{ ...tableHeaderStyle, position: "sticky", top: 0, zIndex: 2 }}>Chilito</th>
                      <th style={{ ...tableHeaderStyle, position: "sticky", top: 0, zIndex: 2 }}>Puesto</th>
                      <th style={{ ...tableHeaderStyle, position: "sticky", top: 0, zIndex: 2 }}>Turno</th>
                      <th style={{ ...tableHeaderStyle, position: "sticky", top: 0, zIndex: 2 }}>Horario Programado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.proximosList && details.proximosList.length > 0 ? (
                      details.proximosList.map((item, idx) => (
                        <tr key={idx} style={{ background: idx % 2 === 0 ? "#FFFFFF" : "#FAFAFA" }}>
                          <td style={{ ...tableCellStyle, fontWeight: 600 }}>
                            {renderPartnerWithIcon(item.partner)}
                          </td>
                          <td style={tableCellStyle}>
                            {item.position?.name || item.positionName}
                          </td>
                          <td style={tableCellStyle}>
                            <span
                              style={{
                                ...getShiftBadgeStyle(item.shift),
                                borderRadius: "999px",
                                padding: "4px 10px",
                                fontSize: "11px",
                                fontWeight: 700,
                              }}
                            >
                              {item.shift}
                            </span>
                          </td>
                          <td style={tableCellStyle}>
                            <span
                              className="badge bg-info"
                              style={{ fontSize: "11px", padding: "6px 8px" }}
                            >
                              {formatScheduleRange(item.startTime, item.endTime)}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={4}
                          style={{
                            textAlign: "center",
                            padding: "20px",
                            height: `${tableMinHeightPx - 52}px`,
                            verticalAlign: "middle",
                          }}
                        >
                          No hay próximos ingresos programados.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {operacionTab === "operacion" && (
            <div>
              <h6
                style={{
                  fontWeight: "700",
                  marginBottom: "12px",
                  color: "#28a745",
                }}
              >
                En Operacion ({inOperation.length})
              </h6>
              <div
                style={{
                  overflowX: "auto",
                  maxHeight: "360px",
                  minHeight: `${tableMinHeightPx}px`,
                  border: "1px solid #E5E7EB",
                  borderRadius: "10px",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "separate",
                    borderSpacing: 0,
                  }}
                >
                  <thead>
                    <tr>
                      <th style={{ ...tableHeaderStyle, position: "sticky", top: 0, zIndex: 2 }}>Chilito</th>
                      <th style={{ ...tableHeaderStyle, position: "sticky", top: 0, zIndex: 2 }}>Puesto</th>
                      <th style={{ ...tableHeaderStyle, position: "sticky", top: 0, zIndex: 2 }}>Turno</th>
                      <th style={{ ...tableHeaderStyle, position: "sticky", top: 0, zIndex: 2 }}>Horario</th>
                      <th style={{ ...tableHeaderStyle, position: "sticky", top: 0, zIndex: 2 }}>Entrada</th>
                      <th style={{ ...tableHeaderStyle, position: "sticky", top: 0, zIndex: 2 }}>Salida</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inOperation.length > 0 ? (
                      inOperation.map((item: any, idx: number) => (
                        <tr key={idx} style={{ background: idx % 2 === 0 ? "#FFFFFF" : "#FAFAFA" }}>
                          <td style={{ ...tableCellStyle, fontWeight: 600 }}>
                            {renderPartnerWithIcon(item.partner)}
                          </td>
                          <td style={tableCellStyle}>
                            {item.position?.name || item.positionName}
                          </td>
                          <td style={tableCellStyle}>
                            <span
                              style={{
                                ...getShiftBadgeStyle(item.shift),
                                borderRadius: "999px",
                                padding: "4px 10px",
                                fontSize: "11px",
                                fontWeight: 700,
                              }}
                            >
                              {item.shift}
                            </span>
                          </td>
                          <td style={tableCellStyle}>
                            <span style={{ color: "#374151", fontWeight: 600 }}>
                              {formatScheduleRange(item.startTime, item.endTime)}
                            </span>
                          </td>
                          <td style={tableCellStyle}>
                            {item.assistanceCheck?.checkTime ? (
                              <span className="badge bg-success" style={{ fontSize: "11px", padding: "6px 8px" }}>
                                {formatSimpleTime(item.assistanceCheck.checkTime)}
                              </span>
                            ) : (
                              <span className="text-muted small">-</span>
                            )}
                          </td>
                          <td style={tableCellStyle}>
                            {item.assistanceCheckout?.checkTime ? (
                              <span className="badge bg-primary" style={{ fontSize: "11px", padding: "6px 8px" }}>
                                {formatSimpleTime(item.assistanceCheckout.checkTime)}
                              </span>
                            ) : (
                              <span className="text-muted small">-</span>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={6}
                          style={{
                            textAlign: "center",
                            padding: "20px",
                            height: `${tableMinHeightPx - 52}px`,
                            verticalAlign: "middle",
                          }}
                        >
                          No hay partners en operación.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {operacionTab === "faltantes" && (
            <div>
            <h6
              style={{
                fontWeight: "700",
                marginBottom: "12px",
                color: "#dc3545",
              }}
            >
              Faltantes por Entrar ({details.faltantesList.length})
            </h6>
            <div
              style={{
                overflowX: "auto",
                minHeight: `${tableMinHeightPx}px`,
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                }}
              >
                <thead>
                  <tr>
                    <th style={tableHeaderStyle}>Chilito</th>
                    <th style={tableHeaderStyle}>Puesto</th>
                    <th style={tableHeaderStyle}>Turno</th>
                    <th style={tableHeaderStyle}>Horario</th>
                  </tr>
                </thead>
                <tbody>
                  {details.faltantesList.length > 0 ? (
                    details.faltantesList.map((item, idx) => (
                      <tr key={idx}>
                        <td style={tableCellStyle}>
                          {renderPartnerWithIcon(item.partner)}
                        </td>
                        <td style={tableCellStyle}>
                          {item.position?.name || item.positionName}
                        </td>
                        <td style={tableCellStyle}>{item.shift}</td>
                        <td style={tableCellStyle}>{item.startTime}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={4}
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          height: `${tableMinHeightPx - 52}px`,
                          verticalAlign: "middle",
                        }}
                      >
                        No hay faltantes registrados a esta hora.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            </div>
          )}
        </div>
      );
    }

    if (modalType === "puestos") {
      // Filtrar los que tienen 0 requeridos y agrupar por nombre de puesto para una vista más limpia
      const groupedPuestos = details.puestosList.reduce((acc: any, p: any) => {
        const required = Number(p.numberOfWorkers) || 0;
        if (required <= 0) return acc;

        const name = p.position?.name || "Sin nombre";
        if (!acc[name]) acc[name] = [];
        acc[name].push(p);
        return acc;
      }, {});

      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {Object.keys(groupedPuestos).length > 0 ? (
            Object.keys(groupedPuestos).map((posName, idx) => (
              <div
                key={idx}
                style={{
                  background: "#ffffff",
                  borderRadius: "12px",
                  padding: "16px",
                  border: "1px solid #e9ecef",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                }}
              >
                <h6
                  style={{
                    fontWeight: "700",
                    color: "#495057",
                    marginBottom: "16px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <i
                    className="fa-solid fa-briefcase"
                    style={{ color: "#343a40", fontSize: "14px" }}
                  ></i>
                  {posName}
                </h6>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "12px",
                  }}
                >
                  {groupedPuestos[posName].map((p: any, pIdx: number) => {
                    // Contar cubiertos específicamente para este puesto y turno
                    const cubiertos = details.operacionList.filter(
                      (op: any) =>
                        (op.position?.id === p.position?.id ||
                          op.positionId === p.position?.id ||
                          Number(op.originalPositionId) === p.position?.id) &&
                        op.shift === p.shift
                    ).length;

                    const isComplete = cubiertos >= p.numberOfWorkers;
                    const color = isComplete ? "#28a745" : "#dc3545";

                    return (
                      <div
                        key={pIdx}
                        style={{
                          padding: "12px",
                          borderRadius: "10px",
                          background: isComplete
                            ? "rgba(40, 167, 69, 0.05)"
                            : "rgba(220, 53, 69, 0.05)",
                          border: `1px solid ${isComplete
                              ? "rgba(40, 167, 69, 0.2)"
                              : "rgba(220, 53, 69, 0.2)"
                            }`,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#6c757d",
                              fontWeight: "600",
                            }}
                          >
                            {p.shift}
                          </div>
                          <div
                            style={{
                              fontSize: "18px",
                              fontWeight: "800",
                              color: color,
                            }}
                          >
                            {cubiertos}
                            <span
                              style={{
                                fontSize: "14px",
                                color: "#adb5bd",
                                margin: "0 4px",
                              }}
                            >
                              /
                            </span>
                            {p.numberOfWorkers}
                          </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <span
                            style={{
                              fontSize: "10px",
                              padding: "2px 8px",
                              borderRadius: "10px",
                              background: color,
                              color: "white",
                              textTransform: "uppercase",
                              fontWeight: "700",
                            }}
                          >
                            {isComplete ? "Ok" : "Falta"}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          ) : (
            <div
              style={{ textAlign: "center", padding: "40px", color: "#6c757d" }}
            >
              No hay puestos requeridos configurados con personal necesario.
            </div>
          )}
        </div>
      );
    }

    if (modalType === "faltas") {
      return (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={tableHeaderStyle}>Partner</th>
                <th style={tableHeaderStyle}>Puesto</th>
                <th style={tableHeaderStyle}>Turno</th>
                <th style={tableHeaderStyle}>Hora Entrada</th>
                <th style={tableHeaderStyle}>Hora Salida</th>
              </tr>
            </thead>
            <tbody>
              {details.faltantesList.length > 0 ? (
                details.faltantesList.map((item, idx) => (
                  <tr key={idx} className="text-center">
                    <td style={tableCellStyle}>
                      {renderPartnerWithIcon(item.partner)}
                    </td>
                    <td style={tableCellStyle}>
                      {item.position?.name || item.positionName}
                    </td>
                    <td style={tableCellStyle}>{item.shift}</td>
                    <td style={tableCellStyle}>{item.startTime.split(" - ")[0]}</td>
                    <td style={tableCellStyle}>{item.endTime.split(" - ")[1]}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={4}
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    No hay faltas registradas.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      );
    }

    if (modalType === "cobertura") {
      return (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={tableHeaderStyle}>Chilito Ausente</th>
                <th style={tableHeaderStyle}>Reemplazo</th>
                <th style={tableHeaderStyle}>Puesto</th>
                <th style={tableHeaderStyle}>Estatus</th>
              </tr>
            </thead>
            <tbody>
              {details.coberturaList.length > 0 ? (
                details.coberturaList.map((item, idx) => (
                  <tr key={idx}>
                    <td style={tableCellStyle}>
                      {renderPartnerWithIcon(item.partner)}
                    </td>
                    <td style={tableCellStyle}>
                      {item.replacement
                        ? renderPartnerWithIcon(item.replacement.partner)
                        : "Sin reemplazo"}
                    </td>
                    <td style={tableCellStyle}>
                      {item.position?.name || item.positionName || "-"}
                    </td>
                    <td style={tableCellStyle}>
                      <span
                        className={`badge bg-${item.replacement ? "success" : "danger"
                          }`}
                      >
                        {item.replacement ? "Cubierta" : "No Cubierta"}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={4}
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    No hay incidencias de cobertura.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      );
    }

    if (modalType === "doble") {
      const list = details.dobleFuncionList || [];
      const formatTime = (p: any) => {
        const start =
          typeof p.startTime === "string"
            ? p.startTime.split(" - ")[0] || p.startTime
            : p.startTime;
        const end =
          typeof p.endTime === "string"
            ? p.endTime.split(" - ").pop() || p.endTime
            : p.endTime;
        return start && end ? `${start} - ${end}` : "";
      };
      return (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={tableHeaderStyle}>Chilito</th>
                <th style={tableHeaderStyle}>Puestos asignados (día actual)</th>
                <th style={tableHeaderStyle}>Horario (día actual)</th>
              </tr>
            </thead>
            <tbody>
              {list.length > 0 ? (
                list.map((item: any) => {
                  const positionsToday = item.days?.[0]?.positions || [];
                  return (
                    <tr key={item.partner?.id}>
                      <td style={tableCellStyle}>
                        {renderPartnerWithIcon(item.partner)}
                      </td>
                      <td style={tableCellStyle}>
                        {positionsToday.map((p: any) => (
                          <div key={p.id || p.name}>{p.name}</div>
                        ))}
                      </td>
                      <td style={tableCellStyle}>
                        {positionsToday.map((p: any) => (
                          <div key={p.id || p.name}>{formatTime(p)}</div>
                        ))}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={3}
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    No hay partners con doble función el día actual.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      );
    }

    return null;
  };

  return (
    <div style={{ width: "100%" }}>
      <Loader isLoading={loading} />

      {/* Header with Search */}
      <div
        className="d-flex justify-content-between align-items-center mb-16px mb-3"
      >
        <div
          style={{
            background: "#f8f9fa",
            display: "flex",
            alignItems: "center",
            width: "100%",
            maxWidth: "480px",
          }}
          className="p-2 rounded-3 gap-2"
        >
          <i
            className="fa-solid fa-magnifying-glass"
            style={{
              color: "#6c757d",
              fontSize: "14px",
              marginLeft: "3px",
            }}
          ></i>
          <input
            type="text"
            className="form-control"
            placeholder="Buscar sucursal..."
            style={{
              border: "none",
              background: "transparent",
              boxShadow: "none",
            }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginLeft: "16px",
            color: "#6c757d",
            fontSize: "12px",
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
          className="d-flex align-items-center gap-2 bg-white p-3 rounded-3"
        >
          <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span
              style={{
                width: "14px",
                height: "14px",
                borderRadius: "4px",
                background: "#28a745",
                display: "inline-block",
              }}
            ></span>
            Verde: 100%
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span
              style={{
                width: "14px",
                height: "14px",
                borderRadius: "4px",
                background: "#ff9800",
                display: "inline-block",
              }}
            ></span>
            Naranja: 80%-99%
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span
              style={{
                width: "14px",
                height: "14px",
                borderRadius: "4px",
                background: "#dc3545",
                display: "inline-block",
              }}
            ></span>
            Rojo: &lt; 80%
          </span>
        </div>
      </div>

      <ModalContainer
        open={modalOpen}
        title={modalTitle}
        closeFn={() => setModalOpen(false)}
        size="xl"
      >
        {renderModalContent()}
      </ModalContainer>

      {/* Tabla de todas las sucursales */}
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          marginBottom: "24px",
          maxHeight: "calc(100vh - 250px)",
          overflow: "auto",
        }}
      >
        <div>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "0.78rem",
            }}
          >
            <thead>
              <tr
                style={{
                  background: " #495057 0",
                }}
              >
                <th
                  rowSpan={2}
                  onClick={() => handleSort("name")}
                  style={{
                    padding: "8px 0",
                    textAlign: "center",
                    fontWeight: "600",
                    color: "white",
                    minWidth: "140px",
                    position: "sticky",
                    top: 0,
                    zIndex: 12,
                    background: "inherit",
                    cursor: "pointer",
                  }}
                >
                  Sucursal {getIconSort("name")}
                </th>
                <th
                  colSpan={4}
                  onClick={() => handleSort("operacion")}
                  style={{
                    padding: "8px 0",
                    textAlign: "center",
                    fontWeight: "600",
                    color: "white",
                    minWidth: "280px",
                    position: "sticky",
                    top: 0,
                    zIndex: 12,
                    background: "inherit",
                    cursor: "pointer",
                  }}
                >
                  Chilitos en operación {getIconSort("operacion")}
                </th>
                <th
                  colSpan={4}
                  onClick={() => handleSort("puestos")}
                  style={{
                    padding: "8px 0",
                    textAlign: "center",
                    fontWeight: "600",
                    color: "white",
                    minWidth: "260px",
                    position: "sticky",
                    top: 0,
                    zIndex: 12,
                    background: "inherit",
                    cursor: "pointer",
                  }}
                >
                  Puestos cubiertos {getIconSort("puestos")}
                </th>
                <th
                  rowSpan={2}
                  onClick={() => handleSort("faltas")}
                  style={{
                    padding: "8px 0",
                    textAlign: "center",
                    fontWeight: "600",
                    color: "white",
                    minWidth: "110px",
                    position: "sticky",
                    top: 0,
                    zIndex: 12,
                    background: "inherit",
                    cursor: "pointer",
                  }}
                >
                  Faltas del día {getIconSort("faltas")}
                </th>
                <th
                  rowSpan={2}
                  onClick={() => handleSort("cobertura")}
                  style={{
                    padding: "8px 0",
                    textAlign: "center",
                    fontWeight: "600",
                    color: "white",
                    minWidth: "130px",
                    position: "sticky",
                    top: 0,
                    zIndex: 12,
                    background: "inherit",
                    cursor: "pointer",
                  }}
                >
                  Cobertura de falta {getIconSort("cobertura")}
                </th>
                <th
                  rowSpan={2}
                  onClick={() => handleSort("doble")}
                  style={{
                    padding: "8px",
                    textAlign: "center",
                    fontWeight: "600",
                    color: "white",
                    minWidth: "120px",
                    position: "sticky",
                    top: 0,
                    zIndex: 12,
                    background: "inherit",
                    cursor: "pointer",
                  }}
                >
                  Chilitos con doble función {getIconSort("doble")}
                </th>
              </tr>
              <tr
                style={{
                  background: " #495057 0",
                }}
              >
                {["M", "I", "V", "Total", "M", "I", "V", "Total"].map(
                  (label, idx) => (
                    <th
                      key={`${label}-${idx}`}
                      style={{
                        padding: "6px 0",
                        textAlign: "center",
                        fontWeight: "600",
                        color: "white",
                        minWidth: "70px",
                        position: "sticky",
                        top: 34,
                        zIndex: 11,
                        background: "inherit",
                        border: "none",
                      }}
                    >
                      {label}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {data.length > 0 ? (
                data.map((item, index) => (
                  <RiesgoOperativoRow
                    item={item}
                    no={index}
                    key={item.id}
                    onShowDetails={handleOpenModal}
                  />
                ))
              ) : (
                <tr>
                  <td
                    colSpan={12}
                    style={{
                      padding: "60px 20px",
                      textAlign: "center",
                      color: "#6c757d",
                    }}
                  >
                    <i
                      className="fa-solid fa-inbox"
                      style={{
                        fontSize: "48px",
                        marginBottom: "16px",
                        display: "block",
                      }}
                    ></i>
                    <p style={{ margin: 0, fontSize: "16px" }}>
                      No hay datos disponibles
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TableRiesgoOperativo;
